'use client';

import Box from '@mui/material/Box';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

export type ThresholdSeverity = 'success' | 'warning' | 'error' | 'neutral';

export interface ThresholdBand {
  /** Upper bound of this band (exclusive). Values strictly less than `at` fall into this band. */
  at: number;
  /** Severity color applied to the filled portion when the current value lands in this band. */
  severity: ThresholdSeverity;
}

export interface ThresholdBarProps {
  /** Current value to plot on the bar. */
  value: number;
  /** Maximum value for the track. Defaults to 100. */
  max?: number;
  /**
   * Bands defining how the fill is colored. The first band whose `at` is greater than `value`
   * supplies the severity. Bands should be sorted ascending by `at`. If `value` exceeds every
   * band's `at`, the last band's severity is used.
   *
   * Example for a "lower is better" metric capped at 75%:
   *   `[{ at: 65, severity: 'success' }, { at: 75, severity: 'warning' }, { at: 100, severity: 'error' }]`
   */
  thresholds: ThresholdBand[];
  /** Optional target / policy tick rendered as a vertical line on the track. */
  targetTick?: number;
  /** Left-side label rendered above the bar. */
  label?: string;
  /** Show formatted value to the right of the label. Defaults to true when `label` is provided. */
  showValue?: boolean;
  /** Format function for the value text. Defaults to `${value}%`. */
  formatValue?: (value: number) => string;
  /** Muted helper text after the value (e.g. "max cap", "min 680"). */
  helper?: string;
  /** Bar height: small=4px, medium=6px. Default medium. */
  size?: 'small' | 'medium';
  /** Additional sx overrides applied to the outer Box. */
  sx?: SxProps<Theme>;
  /**
   * Accessible name for the progressbar. Falls back to `label` when omitted.
   * When neither is supplied the progressbar is marked `aria-hidden` so AT
   * doesn't announce an anonymous value. Always supply `ariaLabel` (or
   * `label`) on metric bars that operators rely on.
   */
  ariaLabel?: string;
}

const SIZE_MAP = {
  small: 4,
  medium: 6,
} as const;

function resolveSeverity(value: number, thresholds: ThresholdBand[]): ThresholdSeverity {
  if (thresholds.length === 0) return 'neutral';
  for (const band of thresholds) {
    if (value < band.at) return band.severity;
  }
  return thresholds[thresholds.length - 1].severity;
}

function severityColor(theme: Theme, severity: ThresholdSeverity): string {
  if (severity === 'neutral') return theme.palette.text.secondary;
  return theme.palette[severity].main;
}

const defaultFormat = (v: number): string => `${v.toFixed(1)}%`;

export function ThresholdBar({
  value,
  max = 100,
  thresholds,
  targetTick,
  label,
  showValue,
  formatValue = defaultFormat,
  helper,
  size = 'medium',
  sx,
  ariaLabel,
}: ThresholdBarProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const safeMax = max > 0 ? max : 100;
  const clamped = Math.min(safeMax, Math.max(0, value));
  const percent = (clamped / safeMax) * 100;
  const tickPercent = targetTick != null ? (Math.min(safeMax, Math.max(0, targetTick)) / safeMax) * 100 : null;

  const severity = resolveSeverity(value, thresholds);
  const fillColor = severityColor(theme, severity);

  const barHeight = SIZE_MAP[size];
  const trackBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const tickBg = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.45)';

  const showValueText = showValue ?? Boolean(label);
  const valueText = formatValue(value);

  const hasHeader = Boolean(label) || showValueText || Boolean(helper);

  return (
    <Box sx={sx}>
      {hasHeader && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 1,
            mb: 0.5,
          }}
        >
          {label && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {label}
            </Typography>
          )}
          {(showValueText || helper) && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, ml: 'auto' }}>
              {showValueText && (
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: fillColor,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {valueText}
                </Typography>
              )}
              {helper && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {helper}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}
      <Box
        // When the consumer hasn't supplied any accessible name we mark the
        // bar `aria-hidden` so screen readers don't announce an anonymous
        // value. The numeric value still renders visually for sighted users.
        {...(ariaLabel || label
          ? {
              role: 'progressbar' as const,
              'aria-valuenow': clamped,
              'aria-valuemin': 0,
              'aria-valuemax': safeMax,
              'aria-label': ariaLabel ?? label,
            }
          : { 'aria-hidden': true })}
        sx={{
          position: 'relative',
          height: barHeight,
          borderRadius: barHeight / 2,
          backgroundColor: trackBg,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: `${percent}%`,
            backgroundColor: fillColor,
            borderRadius: barHeight / 2,
            transition: 'width 200ms ease-out, background-color 200ms ease-out',
          }}
        />
        {tickPercent != null && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: -2,
              bottom: -2,
              left: `${tickPercent}%`,
              width: '1px',
              backgroundColor: tickBg,
              transform: 'translateX(-0.5px)',
              boxShadow: `0 0 0 1px ${alpha(theme.palette.background.default, 0.4)}`,
            }}
          />
        )}
      </Box>
    </Box>
  );
}

ThresholdBar.displayName = 'ThresholdBar';

export default ThresholdBar;
